/**
 * What one survey, in one version, found out.
 *
 * **One survey and one version, always** (R21/R2). There is no "todas as pesquisas" view and there
 * will not be one: a 1–5 rating about the menu and a 0–10 NPS about maps share neither scale nor
 * population. Two versions are not pooled either — that is why `bump-version` clones the
 * questionnaire in the first place: the answers of version 1 were written against the words of
 * version 1.
 *
 * The screen needs a survey in the URL. With no id, the empty state is the list itself, not a
 * blank page with a picker: choosing a survey is what the list is for.
 *
 * **A cut narrows the questions and nothing else** (R49). Choosing a question and one of its
 * answers makes every per-question number count only the people who gave that answer — and leaves
 * the top cards, the funnel, the rate, the eligible audience and the average duration on the full
 * base, because whoever abandoned at step 1 left no answer at all and no cut by an answer reaches
 * them. The screen says that in as many places as it takes: a sticky band while the cut is on, the
 * base printed in each cut card's footer, and a tag on every block that did **not** narrow. The
 * worst outcome available here is somebody reading a cut number as the total.
 *
 * **Every number here counts PEOPLE, and every label says so** (R22b). One response per person per
 * version holds across the whole module since R33, so numerator and denominator of any percentage
 * speak the same unit — and the place to say that is the label of each number, not a footnote at
 * the bottom that nobody reads. The cards, the funnel columns and each question's footer are all
 * worded that way on purpose; "Enviaram: 214" beside "214 respostas" was the ambiguity.
 *
 * @module Pages/BUs/Surveys
 */

import { css } from '@emotion/css';
import { UTButton, UTLoading } from '@group-link-one/gl-ui/utilities';
import { useMemo, useState } from 'react';
import type { JSX } from 'react';
import { QuestionResultCard } from './Components/QuestionResultCard';
import { ResultsComments } from './Components/ResultsComments';
import { ResultsCutControl } from './Components/ResultsCutControl';
import { ResultsCutNotice } from './Components/ResultsCutNotice';
import { ResultsFunnel } from './Components/ResultsFunnel';
import { ResultsOptInBlock } from './Components/ResultsOptInBlock';
import { ResultsRespondents } from './Components/ResultsRespondents';
import { SurveyPurgeDialog } from './Components/SurveyPurgeDialog';
import { SURVEY_PURGE } from '../../Constants/permissions';
import { useExportSurveyCsv } from '../../Hooks/useExportSurveyCsv';
import { usePurgeSurvey } from '../../Hooks/usePurgeSurvey';
import { useSurvey } from '../../Hooks/useSurvey';
import { useSurveyComments } from '../../Hooks/useSurveyComments';
import { useSurveyResponses } from '../../Hooks/useSurveyResponses';
import { useSurveyResults } from '../../Hooks/useSurveyResults';
import type { SurveyResponseStatus, SurveyResultsCut } from '../../Types/Surveys.types';
import {
  CUT_FULL_BASE_NOTICE,
  CUT_FULL_BASE_TAG,
  cutEmptyDescription,
  cutEmptyTitle,
  cuttableQuestions,
  isEmptyCut,
} from '../../Utils/resultsCut';
import {
  countSummary,
  eligibleFootnote,
  eligibleLabel,
  eligibleTooltipLines,
  formatCount,
  formatDuration,
  funnelSummary,
  optInSummary,
  questionsSummary,
  responseRateFootnote,
  responseRateLabel,
  responseRateTooltipLines,
  surveyTitle,
} from '../../Utils/surveyFormat';
import { useProfile } from '@/Auth/contexts/ProfileContext';
import { usePrivileges } from '@/Hooks/usePrivileges/usePrivilleges';
import { CollapsibleSection } from '@/Shared/components/CollapsibleSection/CollapsibleSection';
import { EmptyState } from '@/Shared/components/EmptyState/EmptyState';
import { MetricCard } from '@/Shared/components/MetricCard/MetricCard';
import { MainContent } from '@/Templates/Main/MainContent/MainContent';

const screenStyle = css`
  display: flex;
  flex-direction: column;
  gap: var(--space-base);
`;

const headerStyle = css`
  display: flex;
  align-items: center;
  gap: var(--space-s);
  flex-wrap: wrap;
`;

const identityStyle = css`
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
`;

const titleStyle = css`
  font-size: var(--font-size-lg);
  font-weight: var(--font-weight-semibold);
  text-wrap: pretty;
`;

const slugStyle = css`
  font-family: var(--font-mono);
  font-size: var(--font-size-xs);
  color: var(--color-text-muted);
`;

const spacerStyle = css`
  flex: 1;
`;

const selectStyle = css`
  height: 40px;
  padding: 0 var(--space-s);
  border-radius: var(--radius-control);
  border: 1px solid var(--color-border-strong);
  background: var(--color-surface);
  color: var(--color-text);
  font-size: var(--font-size-sm);
`;

const versionFieldStyle = css`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const labelStyle = css`
  font-size: var(--font-size-2xs);
  font-weight: var(--font-weight-semibold);
  color: var(--color-text-secondary);
`;

const metricsStyle = css`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(min(210px, 100%), 1fr));
  gap: var(--space-base);
`;

const panelHintStyle = css`
  margin: 0;
  font-size: var(--font-size-xs);
  color: var(--color-text-muted);
  text-wrap: pretty;
`;

/**
 * Three columns, and the two wide types take two of them.
 *
 * Five columns left every card too narrow to fit a label and its bar on one line, which is what
 * made the bars stack in the first place. `dense` lets a later small card fill a column a wide one
 * left open.
 *
 * The cards **stretch** to their row rather than sitting at the top of it. A grid row is as tall as
 * its tallest card, so with `start` the short ones left a white crater beside every grid; stretched,
 * the same space becomes air inside a card, and the footer — which is pinned to the bottom — lands
 * on the same line across the row. Nothing about the content changes, only where the slack goes.
 */
const questionsGridStyle = css`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  grid-auto-flow: row dense;
  gap: var(--space-base);
  align-items: stretch;
`;

/** The wrapper has to stretch too, or the card inside it is the only thing that does not. */
const cardCellStyle = css`
  display: flex;
  min-width: 0;

  > * {
    flex: 1;
  }
`;

/** A grid and an NPS carry more to show, so they get the room instead of growing downwards. */
const wideCardStyle = css`
  grid-column: span 2;
`;

/**
 * While the next payload is in flight the old one stays, dimmed.
 *
 * Applying or clearing a cut keeps the previous results on screen instead of blanking everything to
 * a spinner and back: the reader would lose their place, and a screen that flashes empty invites a
 * second click on the control that was just used. The dim covers the **whole** screen because all of
 * it is momentarily stale — the cards and the header's version line included — and marking only the
 * charts would claim the rest had already caught up.
 */
const refetchingStyle = css`
  opacity: 0.55;
  transition: opacity 120ms ease;
`;

/** The mark a block wears to say it did NOT narrow, beside numbers that did. */
const fullBaseTagStyle = css`
  margin: 0;
  font-size: var(--font-size-2xs);
  font-weight: var(--font-weight-semibold);
  color: var(--color-text-secondary);
`;

const errorStyle = css`
  display: flex;
  flex-direction: column;
  gap: var(--space-xs);
  padding: var(--space-ml);
  border: 1px solid var(--color-danger);
  border-left: 3px solid var(--color-danger);
  border-radius: var(--radius-card);
  background: var(--color-danger-subtle);
  font-size: var(--font-size-sm);
`;

const errorTitleStyle = css`
  font-weight: var(--font-weight-semibold);
`;

const COMMENTS_PAGE_SIZE = 25;
const RESPONSES_PAGE_SIZE = 25;

export interface SurveyResultsScreenProps {
  surveyId: string;
  onBack: () => void;
}

export const SurveyResultsScreen = ({
  surveyId,
  onBack,
}: SurveyResultsScreenProps): JSX.Element => {
  const [version, setVersion] = useState<number | undefined>();
  /**
   * The cut: a question and one of its answers, or nothing (R49).
   *
   * One piece of state for the pair, never two, because the two query params travel together —
   * one without the other is a `400` that names no group of people.
   */
  const [cut, setCut] = useState<SurveyResultsCut | undefined>();
  const [commentsPage, setCommentsPage] = useState(1);
  const [questionKey, setQuestionKey] = useState('');
  const [responsesPage, setResponsesPage] = useState(1);
  const [responseStatus, setResponseStatus] = useState('');
  const [minDays, setMinDays] = useState('');
  const [maxDays, setMaxDays] = useState('');

  /**
   * Which sections are open.
   *
   * Questions is the screen's subject, so it opens; the funnel and the opt-in block are short and
   * frame everything below them, so they open too. Respondents and comments are long lists nobody
   * asked for yet.
   *
   * Collapsing only stops the section rendering — the queries live here, in the screen, so nothing
   * is cancelled and nothing refetches when it comes back. It also means the collapsed summaries
   * are the same data the open section shows, never a guess.
   *
   * The choice is not remembered between visits: this panel persists no view preference anywhere,
   * and inventing a mechanism for one screen would be the first of several.
   */
  const [openSections, setOpenSections] = useState({
    funnel: true,
    optIn: true,
    questions: true,
    respondents: false,
    comments: false,
  });

  const toggleSection = (section: keyof typeof openSections): void => {
    setOpenSections(current => ({ ...current, [section]: !current[section] }));
  };

  const { profile } = useProfile();
  const { pages: privileges } = usePrivileges();

  /*
    Two gates, as everywhere else in this panel: the flag says the screen offers erasing a
    respondent, `survey:purge` on the profile says the BFF would allow it. Either one missing hides
    the button — a flag alone would render something that answers 403, and a permission alone would
    ignore the switch in Flagsmith. `survey:purge` is held by super_admin only; `admin` and
    `research_manager` do not have it, on purpose.
  */
  const canErase =
    privileges.BUSINESS_UNITS.SURVEYS.PURGE &&
    (profile?.permissions?.includes(SURVEY_PURGE) ?? false);

  const { data: survey, refetch: refetchSurvey } = useSurvey(surveyId);
  const {
    data: results,
    isLoading,
    isFetching,
    error,
  } = useSurveyResults(surveyId, version, true, cut);
  const exportCsv = useExportSurveyCsv();
  const purge = usePurgeSurvey(surveyId);

  /** Open only while the purge dialog is up. It needs the survey, for the slug and the count. */
  const [isPurging, setIsPurging] = useState(false);

  /*
    The same cut, on the feed. Reading what THAT group wrote is half the value of having the group,
    and `/results` carries no text at all — only how many people wrote something.

    `questionKey` and the cut are different filters and combine: which question is in the feed, and
    whose text is in it.
  */
  const { data: commentsPageData, isLoading: isLoadingComments } = useSurveyComments(surveyId, {
    version,
    page: commentsPage,
    limit: COMMENTS_PAGE_SIZE,
    ...(questionKey ? { questionKey } : {}),
    ...cut,
  });

  const { data: responsesPageData, isLoading: isLoadingResponses } = useSurveyResponses(surveyId, {
    version,
    page: responsesPage,
    limit: RESPONSES_PAGE_SIZE,
    ...(responseStatus ? { status: responseStatus as SurveyResponseStatus } : {}),
  });

  /** Every version that ever existed, newest first. The delivered one is the default. */
  const versionOptions = useMemo(() => {
    const highest = survey ? Math.max(survey.version, survey.draftVersion ?? 0) : 1;

    return Array.from({ length: highest }, (_item, index) => highest - index);
  }, [survey]);

  /**
   * What the cut may be made on, in the version on screen (R49).
   *
   * Built from the payload the screen is already showing, so it can only ever offer a question of
   * **this** version — the cut never crosses versions. It survives a cut being applied because the
   * BFF returns every question and every option under a cut, zeroed rather than dropped; that is
   * what keeps the picker usable from inside a cut that caught nobody.
   */
  const cutQuestions = useMemo(() => cuttableQuestions(results?.questions), [results]);

  /** Only the questions whose answers are text — the ones the comment filter can narrow to. */
  const textQuestions = useMemo(
    () =>
      (results?.questions ?? [])
        .filter(
          question =>
            question.type === 'SHORT_TEXT' ||
            question.type === 'LONG_TEXT' ||
            question.type === 'MULTI_CHOICE' ||
            question.type === 'SINGLE_CHOICE',
        )
        .map(question => ({ key: question.key, label: question.label })),
    [results],
  );

  if (isLoading) {
    return (
      <MainContent id='survey-results-main' styling={{ noBackgroundColor: true, noBorder: true }}>
        <UTLoading type='dots' />
      </MainContent>
    );
  }

  if (error || !results) {
    return (
      <MainContent id='survey-results-main' styling={{ noBackgroundColor: true, noBorder: true }}>
        <div id='survey-results-error' className={errorStyle} role='alert'>
          <span className={errorTitleStyle}>Não foi possível carregar os resultados</span>
          <span>{error?.message ?? 'Pesquisa não encontrada.'}</span>
        </div>
      </MainContent>
    );
  }

  return (
    <MainContent id='survey-results-main' styling={{ noBackgroundColor: true, noBorder: true }}>
      <div className={`${screenStyle} ${isFetching ? refetchingStyle : ''}`} aria-busy={isFetching}>
        <div className={headerStyle}>
          <div className={identityStyle}>
            <span className={titleStyle}>{survey ? surveyTitle(survey) : results.slug}</span>
            <span className={slugStyle}>
              {results.slug} · versão {results.version}
            </span>
          </div>
          <div className={spacerStyle} />

          <div className={versionFieldStyle}>
            <span className={labelStyle}>Versão</span>
            <select
              id='survey-results-version'
              className={selectStyle}
              value={version === undefined ? '' : String(version)}
              onChange={event => {
                setVersion(event.target.value === '' ? undefined : Number(event.target.value));
                setCommentsPage(1);
                setResponsesPage(1);
                /*
                  Changing version CLEARS the cut, rather than revalidating it.

                  A cut never crosses versions (R2): the question may not exist in the other one, or
                  may exist with the same key and other words — `bump-version` clones the
                  questionnaire precisely because the answers of v1 were written against the words
                  of v1. Revalidating would mean carrying the pair over, asking, and then either
                  keeping it or dropping it once the payload arrived — and in between the screen
                  would print cut numbers under a question label from a different version, which is
                  the one thing this feature exists to prevent. Clearing costs one extra click and
                  cannot lie.
                */
                setCut(undefined);
              }}
            >
              <option value=''>Versão entregue ({survey?.version ?? results.version})</option>
              {versionOptions.map(option => (
                <option key={option} value={option}>
                  Versão {option}
                </option>
              ))}
            </select>
          </div>

          <ResultsCutControl
            id='survey-results-cut'
            questions={cutQuestions}
            cut={cut}
            onChange={next => {
              setCut(next);
              setCommentsPage(1);
            }}
          />

          <UTButton
            fitContent
            variant='secondary'
            id='survey-results-export'
            text='Baixar CSV'
            icon='Misc.FileCsv'
            loading={exportCsv.isPending}
            onClick={() => {
              exportCsv.mutate({ id: surveyId, slug: results.slug, version });
            }}
          />
          {canErase ? (
            /*
              Only with the permission, and only once the survey detail is here: the dialog has to
              state how many rows go, and that number is the survey's own `responseCount` — every
              version, every status, exactly what the purge deletes. The cards above cover one
              version, so taking the figure from them would understate the damage on a survey that
              has been bumped.
            */
            <UTButton
              fitContent
              variant='secondary'
              id='survey-results-purge'
              text='Apagar respostas'
              icon='Misc.Trash'
              state='danger'
              disabled={survey === undefined}
              onClick={() => {
                /*
                  The count in the dialog is the survey's `responseCount`, and it is only as fresh
                  as the last time the detail was fetched — answers arrive from people, not from
                  this screen. Refetching on the way in is what keeps "apagar 214 respostas" from
                  being a number somebody read an hour ago.
                */
                void refetchSurvey();
                setIsPurging(true);
              }}
            />
          ) : null}
          <UTButton
            fitContent
            variant='tertiary'
            id='survey-results-back'
            text='Voltar'
            icon='Arrows.ArrowLeft'
            onClick={onBack}
          />
        </div>

        {results.filter ? (
          <ResultsCutNotice
            id='survey-results-cut-notice'
            filter={results.filter}
            fullBaseNotice={CUT_FULL_BASE_NOTICE}
            /* The export button is right above it, so the file's scope has to be stated here. */
            withCsvNotice
            onClear={() => {
              setCut(undefined);
              setCommentsPage(1);
            }}
          />
        ) : null}

        <div className={metricsStyle}>
          <MetricCard
            id='survey-results-eligible'
            label='Pessoas elegíveis'
            value={eligibleLabel(results.eligibilityScope, results.totalEligible)}
            icon='Users.Users'
            footnote={eligibleFootnote(results.eligibilityScope)}
            hint={eligibleTooltipLines({
              scope: results.eligibilityScope,
              totalEligible: results.totalEligible,
              survey,
              optIn: results.optIn,
            })}
          />
          <MetricCard
            id='survey-results-submitted'
            label='Pessoas que enviaram'
            value={formatCount(results.totalSubmitted)}
            icon='Communication.CheckCircle'
            tone='success'
          />
          <MetricCard
            id='survey-results-rate'
            label='Taxa de resposta'
            value={responseRateLabel(results.eligibilityScope, results.responseRate)}
            icon='Misc.ChartLineUp'
            tone='primary'
            footnote={responseRateFootnote(results.eligibilityScope)}
            hint={responseRateTooltipLines({
              scope: results.eligibilityScope,
              totalEligible: results.totalEligible,
              totalSubmitted: results.totalSubmitted,
            })}
          />
          <MetricCard
            id='survey-results-in-progress'
            label='Pessoas que começaram e não enviaram'
            value={formatCount(results.totalInProgress)}
            icon='Timetable.Clock'
            footnote='Abriram e não enviaram. Métrica de abandono, não entra em média nenhuma.'
          />
          <MetricCard
            id='survey-results-dismissed'
            label='Pessoas que dispensaram'
            value={formatCount(results.totalDismissed)}
            icon='Notifications.MinusCircle'
            footnote='Fecharam sem responder. Métrica de recusa.'
          />
          <MetricCard
            id='survey-results-duration'
            label='Tempo médio'
            value={formatDuration(results.avgDurationMs)}
            icon='Timetable.Clock'
            footnote='Só das pessoas que enviaram.'
          />
        </div>

        {/*
          Said again right under the cards, because this is where the mistake would happen: six
          numbers in a row, a cut band above them, and nothing on the cards themselves saying they
          are the only thing on this screen that did not move.
        */}
        {results.filter ? (
          <p id='survey-results-metrics-full-base' className={fullBaseTagStyle}>
            {CUT_FULL_BASE_TAG} — estes seis cartões contam todas as pessoas, não só as do recorte.
          </p>
        ) : null}

        <CollapsibleSection
          id='survey-results-section-funnel'
          title='Funil por passo'
          icon='Misc.ChartBarHorizontal'
          count={funnelSummary(results)}
          summary={results.filter ? CUT_FULL_BASE_TAG : undefined}
          open={openSections.funnel}
          onToggle={() => {
            toggleSection('funnel');
          }}
        >
          <ResultsFunnel id='survey-results-funnel' funnel={results.funnel} />
        </CollapsibleSection>

        {results.optIn ? (
          <CollapsibleSection
            id='survey-results-section-opt-in'
            title='Contexto da funcionalidade opt-in'
            icon='Misc.Lightbulb'
            count={optInSummary(results.optIn)}
            summary={
              results.filter
                ? `${results.optIn.featureKey} · ${CUT_FULL_BASE_TAG}`
                : results.optIn.featureKey
            }
            open={openSections.optIn}
            onToggle={() => {
              toggleSection('optIn');
            }}
          >
            <p className={panelHintStyle}>
              Esta pesquisa só foi entregue a quem estava no estado escolhido desta funcionalidade.
            </p>
            <ResultsOptInBlock id='survey-results-opt-in' optIn={results.optIn} />
          </CollapsibleSection>
        ) : null}

        <CollapsibleSection
          id='survey-results-section-questions'
          title='Perguntas'
          icon='Communication.ChatCircleText'
          count={questionsSummary(results.questions)}
          summary={results.filter ? 'recortado' : undefined}
          open={openSections.questions}
          onToggle={() => {
            toggleSection('questions');
          }}
        >
          {/*
            A cut that caught nobody is a RESULT, and it is said in words — never an error state,
            and never a wall of zeroed bars. The BFF answers `200` with every question at zero
            (R49), and drawing those bars would read as "as pessoas responderam e não escolheram
            nada", which is a different and false finding. The control stays where it is, so the
            way out is one click from the sentence.
          */}
          {isEmptyCut(results.filter) && results.filter ? (
            <EmptyState
              id='survey-results-cut-empty'
              icon='Users.UserFocus'
              title={cutEmptyTitle}
              description={cutEmptyDescription(results.filter)}
            />
          ) : results.questions.length === 0 ? (
            <EmptyState
              id='survey-results-empty'
              icon='Communication.ChatCircleText'
              title='Ainda não há respostas nesta versão'
              description='Assim que alguém enviar, os números aparecem aqui — por pergunta, nesta versão, sem mistura com nenhuma outra pesquisa.'
            />
          ) : (
            <div id='survey-results-questions' className={questionsGridStyle}>
              {results.questions.map(question => (
                <div
                  key={question.key}
                  className={`${cardCellStyle} ${
                    question.type === 'MATRIX' || question.type === 'NPS' ? wideCardStyle : ''
                  }`}
                >
                  <QuestionResultCard
                    question={question}
                    cut={results.filter}
                    onReadComments={key => {
                      setQuestionKey(key);
                      setCommentsPage(1);
                      setOpenSections(current => ({ ...current, comments: true }));
                      window.requestAnimationFrame(() => {
                        document
                          .getElementById('survey-results-comments')
                          ?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                      });
                    }}
                  />
                </div>
              ))}
            </div>
          )}
        </CollapsibleSection>

        <CollapsibleSection
          id='survey-results-section-respondents'
          title='Respondentes'
          icon='Users.Users'
          count={countSummary(responsesPageData?.total ?? 0, 'resposta', 'respostas')}
          /* `GET /:id/responses` does not take the cut at all — this list is always everybody. */
          summary={results.filter ? CUT_FULL_BASE_TAG : undefined}
          open={openSections.respondents}
          onToggle={() => {
            toggleSection('respondents');
          }}
        >
          <ResultsRespondents
            surveyId={surveyId}
            canDelete={canErase}
            responses={responsesPageData?.responses ?? []}
            total={responsesPageData?.total ?? 0}
            page={responsesPage}
            pageSize={RESPONSES_PAGE_SIZE}
            isLoading={isLoadingResponses}
            status={responseStatus}
            minDays={minDays}
            maxDays={maxDays}
            onStatusChange={value => {
              setResponseStatus(value);
              setResponsesPage(1);
            }}
            onMinDaysChange={setMinDays}
            onMaxDaysChange={setMaxDays}
            onPageChange={setResponsesPage}
          />
        </CollapsibleSection>

        {isPurging && survey ? (
          <SurveyPurgeDialog
            slug={survey.slug}
            responseCount={survey.responseCount}
            isWorking={purge.isPending}
            onClose={() => {
              setIsPurging(false);
            }}
            onPurge={confirmSlug => {
              /*
                Nothing on screen changes before the `200`. The numbers move when the refetch brings
                them back; a `400` — the slug did not match — deleted nothing, and the screen has to
                keep saying so.
              */
              purge.mutate(confirmSlug, {
                onSettled: () => {
                  setIsPurging(false);
                },
              });
            }}
          />
        ) : null}

        <div id='survey-results-comments'>
          <CollapsibleSection
            id='survey-results-section-comments'
            title='Comentários'
            icon='Communication.ChatsCircle'
            count={countSummary(commentsPageData?.total ?? 0, 'comentário', 'comentários')}
            summary={commentsPageData?.filter ? 'recortado' : undefined}
            open={openSections.comments}
            onToggle={() => {
              toggleSection('comments');
            }}
          >
            <p className={panelHintStyle}>
              O texto livre não entra nos cartões acima — eles dizem quantas pessoas escreveram,
              nunca o quê. Só respostas enviadas aparecem aqui.
              {results.filter
                ? ' Com um recorte aplicado, esta lista é o que aquele grupo escreveu — ler os textos dele é metade do valor do recorte.'
                : ''}
            </p>
            <ResultsComments
              comments={commentsPageData?.comments ?? []}
              total={commentsPageData?.total ?? 0}
              page={commentsPage}
              pageSize={COMMENTS_PAGE_SIZE}
              isLoading={isLoadingComments}
              questionKey={questionKey}
              questionOptions={textQuestions}
              cut={commentsPageData?.filter}
              onQuestionKeyChange={value => {
                setQuestionKey(value);
                setCommentsPage(1);
              }}
              onPageChange={setCommentsPage}
            />
          </CollapsibleSection>
        </div>
      </div>
    </MainContent>
  );
};
